<script setup>
import { ref, watch } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { getI18n } from '@/plugins/i18n'

import { useGridRef, useDefaultGridOptions, useLookup, refreshGridAsync } from '@/composables/data-grid'
import { createGridLink } from '@/composables/data-grid-column'
import { snoozePresets, useNotifications } from '@/composables/notifications'

import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import NotificationSnoozeMenu from '@/components/NotificationSnoozeMenu.vue'

const gridId = 'notification-grid-v6'

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const {
  notificationTypeChips,
  hasTarget,
  notificationTargetIcon,
  notificationTargetLabel,
  notificationMessage,
  markAsReadAsync,
  markAsUnreadAsync,
  markAllAsReadAsync,
  snoozeAsync,
  toggleReadAsync,
  openNotificationAsync,
  useNotificationsDataSource,
  loadLatestNotificationsAsync,
} = useNotifications()

const { gridRef } = useGridRef()

const gridDataSource = useNotificationsDataSource()

const toolbarButtons = { read: null, unread: null, snooze: null }
const SELECTION_BUTTON_LABELS = Object.freeze({ unread: 'common.markAsUnread', snooze: 'common.snooze' })

const selectedIds = () => gridRef.value?.instance?.getSelectedRowKeys() ?? []

const updateToolbarButtons = () => {
  const selectedCount = selectedIds().length
  toolbarButtons.read?.option({
    text: selectedCount > 0 ? `${i18n.t('common.markAsRead')} (${selectedCount})` : i18n.t('common.markAllAsRead'),
    disabled: selectedCount === 0 && webSiteStore.notificationsCount === 0,
  })
  Object.entries(SELECTION_BUTTON_LABELS).forEach(([key, label]) => toolbarButtons[key]?.option({
    text: `${i18n.t(label)} (${selectedCount})`,
    visible: selectedCount > 0,
  }))
}

const refreshAndHighlightNewestAsync = async() => {
  const [newest] = await loadLatestNotificationsAsync({ take: 1, unreadOnly: webSiteStore.notificationsUnreadOnly })
  await refreshGridAsync(gridRef, newest?.id)
}

watch(() => webSiteStore.notificationsCount, updateToolbarButtons)

watch(() => webSiteStore.notificationsArrivedAt, refreshAndHighlightNewestAsync)

watch(() => webSiteStore.notificationsChangedAt, () => gridRef.value?.instance?.refresh())

const unreadChips = [{ id: 'unread', name: i18n.t('common.unreadNotification'), color: 'primary', textColor: 'white' }]

const snoozeMenuOpen = ref(false)
const snoozeAnchor = ref(null)
const snoozeRow = ref(null)

const openRowSnoozeMenu = (e) => {
  snoozeRow.value = e.row.data
  snoozeAnchor.value = e.event.currentTarget
  snoozeMenuOpen.value = true
}

const columns = [
  {
    type: 'buttons',
    buttons: [
      {
        icon: 'mdi mdi-email-open-outline',
        hint: i18n.t('common.markAsRead'),
        visible: (e) => !e.row?.data?.is_read,
        onClick: (e) => onRowToggleRead(e.row.data),
      },
      {
        icon: 'mdi mdi-email-mark-as-unread',
        hint: i18n.t('common.markAsUnread'),
        visible: (e) => !!e.row?.data?.is_read,
        onClick: (e) => onRowToggleRead(e.row.data),
      },
      {
        icon: 'mdi mdi-bell-sleep-outline',
        hint: i18n.t('common.snooze'),
        onClick: openRowSnoozeMenu,
      },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '5rem',
  },
  {
    dataField: 'is_read',
    caption: '',
    width: '7rem',
    dataType: 'boolean',
    alignment: 'center',
    allowFiltering: false,
    showInColumnChooser: false,
    cellTemplate: 'read-cell-template',
  },
  {
    dataField: 'visible_from',
    caption: i18n.t('common.date'),
    width: 'auto',
    dataType: 'datetime',
    sortOrder: 'desc',
  },
  {
    dataField: 'category',
    caption: i18n.t('common.category'),
    width: 'auto',
    lookup: useLookup({ store: portalStore.getLocalizedNotificationCategories(), displayExpr: 'name' }),
  },
  {
    dataField: 'type',
    caption: i18n.t('common.type'),
    width: 'auto',
    lookup: useLookup({ store: portalStore.getLocalizedNotificationTypes(), displayExpr: 'name', sort: 'name' }),
    cellTemplate: 'type-cell-template',
  },
  {
    dataField: 'message',
    caption: i18n.t('common.message'),
    width: '50%',
    calculateDisplayValue: notificationMessage,
  },
  {
    name: 'target',
    caption: i18n.t('common.open'),
    width: 'auto',
    allowFiltering: false,
    allowSorting: false,
    cellTemplate: (element, { data }) => {
      if (!hasTarget(data)) return
      element.appendChild(createGridLink({
        icon: notificationTargetIcon(data),
        text: notificationTargetLabel(data),
        onClick: () => openNotificationAsync(data),
      }))
    },
  },
]

const clearSelectionAndRefreshAsync = async() => {
  gridRef.value.instance.clearSelection()
  await gridRef.value.instance.refresh()
}

const onMarkAsRead = async() => {
  const ids = selectedIds()
  gridRef.value.instance.clearSelection()
  await (ids.length > 0 ? markAsReadAsync(ids) : markAllAsReadAsync())
}

const onMarkAsUnread = async() => {
  const ids = selectedIds()
  gridRef.value.instance.clearSelection()
  await markAsUnreadAsync(ids)
}

const onSnooze = async(until) => {
  const ids = selectedIds()
  gridRef.value.instance.clearSelection()
  await snoozeAsync(ids, until)
}

const onRowToggleRead = (notification) => toggleReadAsync(notification)

const onRowSnooze = (notification, until) => snoozeAsync([notification.id], until)

const onUnreadOnlyChange = (value) => {
  webSiteStore.notificationsUnreadOnly = value
  clearSelectionAndRefreshAsync()
}

const toolbarWidget = (key, widget, options) => ({
  widget,
  location: 'after',
  options: {
    ...options,
    onInitialized: (e) => {
      toolbarButtons[key] = e.component
      updateToolbarButtons()
    },
  },
})

const onCellPrepared = (e) => {
  if (e.rowType === 'data' && e.column.dataField === 'message' && !e.data.is_read) e.cellElement.classList.add('text-bold')
}

const gridOptions = useDefaultGridOptions({
  id: gridId,
  columns,
  dataSource: gridDataSource,
  editing: {
    allowAdding: false,
    allowEditing: false,
    allowDeleting: false,
  },
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  dataActionWidgets: [
    toolbarWidget('read', 'dxButton', { icon: 'mdi mdi-check-all', onClick: onMarkAsRead }),
    toolbarWidget('unread', 'dxButton', { icon: 'mdi mdi-email-mark-as-unread', onClick: onMarkAsUnread }),
    toolbarWidget('snooze', 'dxDropDownButton', {
      icon: 'mdi mdi-bell-sleep-outline',
      useSelectMode: false,
      visible: false,
      items: snoozePresets(),
      displayExpr: 'label',
      dropDownOptions: { width: 'auto' },
      onItemClick: (e) => onSnooze(e.itemData.until()),
    }),
  ],
  viewToolbarItems: [{ location: 'after', template: 'unread-only-toggle' }],
  onSelectionChanged: updateToolbarButtons,
  onCellPrepared,
  clearFilterButton: true,
})
</script>

<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #unread-only-toggle>
        <q-toggle
          :model-value="webSiteStore.notificationsUnreadOnly"
          :label="`${i18n.t('common.showUnreadOnly')} (${webSiteStore.notificationsCount})`"
          color="primary"
          @update:model-value="onUnreadOnlyChange"
        />
      </template>
      <template #read-cell-template="{ data: templateOptions }">
        <chip-template
          v-if="!templateOptions.data.is_read"
          :values="unreadChips"
          justify="center"
        />
      </template>
      <template #type-cell-template="{ data: templateOptions }">
        <chip-template :values="notificationTypeChips(templateOptions.data)" />
      </template>
    </dx-data-grid>
    <notification-snooze-menu
      v-model="snoozeMenuOpen"
      :target="snoozeAnchor"
      no-parent-event
      @snooze="until => onRowSnooze(snoozeRow, until)"
    />
  </div>
</template>

